// Texty jako PRVNÍ import: moduly se vyhodnocují v pořadí importů a
// ``@/i18n`` sám texty nenese (KAN-223, viz ``i18n/resources.ts``).
import "@/i18n/resources";

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";

import "@/styles/globals.css";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PaywallProvider } from "@/components/PaywallProvider";
import { adoptClaimFromUrl } from "@/embed/guestSession";
import { adoptCustomerPreviewFromUrl } from "@/lib/customerPreview";
import { adoptTrialTokenFromUrl } from "@/lib/storefrontTrial";
import { retryOnlyTransient } from "@/lib/api";
import { installChunkReloadListener } from "@/lib/chunkReload";
import { refreshReadinessAfterWrites } from "@/lib/readinessFreshness";
import { initSentry } from "@/lib/sentry";
import { initWebAnalytics } from "@/lib/webAnalytics";
import { router } from "@/router";

// Fire and forget — the init is async (dynamic import) but we don't
// want to block the first render on it. If DSN is unset this is a
// zero-cost no-op.
void initSentry();

// Návštěvnost a rychlost (Vercel Web Analytics + Speed Insights) — jen
// produkční build na Vercelu.
initWebAnalytics();

// Recover from stale lazy-import chunks after a redeploy. Without
// this, an admin tab open across a Vercel deploy crashes on the next
// route change with ``Failed to fetch dynamically imported module``;
// the listener catches Vite's preload-error event and triggers a
// one-shot reload to fetch the fresh ``index.html`` + hashes.
installChunkReloadListener();

// A visitor arriving from an embedded configurator carries a one-time
// cart handoff token in ``#claim=`` (KAN-1258). Redeem it before the
// first render — the router may fire a cart/quote request immediately,
// and one that goes out without the handle shows an empty cart and
// loses the upload. The fragment is scrubbed from the address bar in
// the same call. Resolves at once when there is nothing to redeem, so
// an ordinary visit is not delayed.
const claimAdopted = adoptClaimFromUrl();

// Stejný důvod, jiný přepínač: náhled „zobrazit jako zákazník"
// (KAN-747) rozhoduje o tom, jestli první request odejde se session
// cookie, nebo bez ní. Zapnout ho až v komponentě by znamenalo, že
// prvních pár dotazů obchod uvidí adminovýma očima.
adoptCustomerPreviewFromUrl();

// Totéž pro pozvánku „vyzkoušej si svůj obchod" (KAN-761): odkaz z
// checklistu se otevírá na telefonu, kde o zkušebním nákupu neví nic
// jiného. Přijmout ji až v komponentě by znamenalo, že token nemusí
// být na místě, než návštěvník stihne první krok cesty.
adoptTrialTokenFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Aggressive caching defaults; specific queries can override.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 4xx se neopakuje — je to odpověď, ne výpadek (KAN-707).
      retry: retryOnlyTransient,
    },
  },
});
// KAN-2011 — každý úspěšný zápis označí Připravenost obchodu za zastaralou.
refreshReadinessAfterWrites(queryClient);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found in index.html");

function render(): void {
  ReactDOM.createRoot(rootEl as HTMLElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* ThemeProvider + AccentProvider live under the router
              (``ShellProviders`` in router.tsx), so the hop interstitials
              run without a session probe (KAN-1207). */}
          <PaywallProvider>
            <RouterProvider router={router} />
          </PaywallProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

// ``adoptClaimFromUrl`` never rejects; the second arm only guards the
// first render against a surprise.
void claimAdopted.then(render, render);
